import { useEffect, useRef, useCallback, useState } from 'react';
import { API_BASE_URL } from '../config';
import { WS_MESSAGES, AI_STATUS, STORAGE_KEYS } from '../constants';

/**
 * WebSocket hook for real-time updates
 * Automatically connects when user is authenticated and reconnects on disconnect
 *
 * @param {object} options
 * @param {function} options.onTranscriptionUpdate - Callback when transcription status changes
 * @param {function} options.onTitleGenerationStarted - Callback when title generation starts
 * @param {function} options.onTitleUpdate - Callback when title is generated
 * @param {boolean} options.enabled - Whether WebSocket should be connected
 */
function useWebSocket({ onTranscriptionUpdate, onTitleGenerationStarted, onTitleUpdate, onEnhancementUpdate, enabled = true }) {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  // Get WebSocket URL based on API_BASE_URL from config
  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return null;

    // Convert API_BASE_URL (http(s)://...) to WebSocket URL (ws(s)://...)
    // API_BASE_URL is like "http://localhost:3000" or "https://bringstomind.com"
    const wsUrl = API_BASE_URL.replace(/^http/, 'ws');

    const fullUrl = `${wsUrl}/ws?token=${encodeURIComponent(token)}`;
    console.log('WebSocket: URL is', fullUrl);
    return fullUrl;
  }, []);

  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.log('WebSocket: No token available, skipping connection');
      return;
    }

    // Don't connect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    console.log('WebSocket: Connecting...');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket: Connected');
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case WS_MESSAGES.CONNECTED:
              console.log('WebSocket: Server acknowledged connection');
              break;

            case WS_MESSAGES.TRANSCRIPTION_UPDATE:
              if (onTranscriptionUpdate) {
                // Parse reflectionId as integer to match frontend ID types
                const reflectionId = parseInt(data.reflectionId, 10);
                // Only include fields that are actually present in the message
                // to avoid overwriting existing data with undefined
                const update = { id: reflectionId };
                if (data.video_transcription_status !== undefined) {
                  update.video_transcription_status = data.video_transcription_status;
                }
                if (data.video_transcription_text !== undefined) {
                  update.video_transcription_text = data.video_transcription_text;
                }
                if (data.audio_transcription_status !== undefined) {
                  update.audio_transcription_status = data.audio_transcription_status;
                }
                if (data.audio_transcription_text !== undefined) {
                  update.audio_transcription_text = data.audio_transcription_text;
                }
                onTranscriptionUpdate(update);
              }
              break;

            case WS_MESSAGES.TITLE_GENERATION_STARTED:
              if (onTitleGenerationStarted) {
                const reflectionId = parseInt(data.reflectionId, 10);
                onTitleGenerationStarted({
                  id: reflectionId,
                  source: data.source  // 'transcription' or 'text'
                });
              }
              break;

            case WS_MESSAGES.TITLE_UPDATE:
              if (onTitleUpdate) {
                const reflectionId = parseInt(data.reflectionId, 10);
                onTitleUpdate({
                  id: reflectionId,
                  title: data.title,
                  source: data.source  // 'transcription' or 'text'
                });
              }
              break;

            case WS_MESSAGES.ENHANCEMENT_STARTED:
              if (onEnhancementUpdate) {
                const reflectionId = parseInt(data.reflectionId, 10);
                onEnhancementUpdate({
                  id: reflectionId,
                  ai_followup_status: AI_STATUS.GENERATING
                });
              }
              break;

            case WS_MESSAGES.ENHANCEMENT_UPDATE:
              if (onEnhancementUpdate) {
                const reflectionId = parseInt(data.reflectionId, 10);
                onEnhancementUpdate({
                  id: reflectionId,
                  ai_followup_status: data.status,
                  ai_followup_suggestions: data.suggestions
                });
              }
              break;

            case WS_MESSAGES.PONG:
              // Heartbeat response
              break;

            default:
              console.log('WebSocket: Unknown message type', data.type);
          }
        } catch (e) {
          console.error('WebSocket: Failed to parse message', e);
        }
      };

      ws.onclose = (event) => {
        console.log(`WebSocket: Disconnected (code: ${event.code})`);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect after 5 seconds (unless deliberately closed)
        if (enabled && event.code !== 4001 && event.code !== 4002) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('WebSocket: Attempting reconnect...');
            connect();
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket: Error', error);
      };

    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
    }
  }, [getWebSocketUrl, onTranscriptionUpdate, onTitleGenerationStarted, onTitleUpdate, onEnhancementUpdate, enabled]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Connect/disconnect based on enabled prop
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  // Send heartbeat every 30 seconds to keep connection alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected]);

  return {
    isConnected,
    reconnect: connect
  };
}

export default useWebSocket;
